import * as mui from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { Select, TextField } from 'formik-mui'
import React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { langSwitchContext, translationsContext } from 'context/translations'
import { Link } from 'components/Link'
import { userContext } from 'context/user'
import { routes } from 'routes'
import { graphqlClient, requireErrorExtensions , useQuery} from 'utils/graphql'

import logoIcon from '../assets/logo.png'
import { LanguageSelector } from '../LanguageSelector'


export function Register() {
  const t = React.useContext(translationsContext)
  const [language] = React.useContext(langSwitchContext)

  React.useEffect(() => {
    if (window.GLOBAL_CONFIG.ENV !== 'prod') return

    const script = document.createElement('script')
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-W9BVJQT');`
    document.head.appendChild(script)

    return () => {document.head.removeChild(script)}
  }, [])

  const validationSchema = Yup.object().shape({
    adisseoContactName: Yup.string().required(t('validation__required')),
    email: Yup.string().email().required(t('validation__required')),
    company: Yup.string().required(t('validation__required')),
    country: Yup.string().required(t('validation__required')),
    name: Yup.string().required(t('validation__required')),
    password: Yup.string().required(t('validation__required')).min(8, t('validation__short_password')),
    password2: Yup.string().required(t('validation__required')).oneOf([Yup.ref('password')], t('validation__password_mismatch')),
  })
  type valuesType = Yup.InferType<typeof validationSchema>

  const [commonErrors, setCommonErrors] = React.useState<string[]>([])
  const [fieldErrors, setFieldErrors] = React.useState<{[k: string]: string}>({})

  const navigate = useNavigate()

  const user = React.useContext(userContext)
  const countries = useQuery(
    {countries: [{language: language}, {__scalar: 1}]},
    'countries'
  )

  React.useEffect(() => {
    countries.refetch()
  }, [language])

  function handleSubmit(
    values: valuesType,
    {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}
  ) {
    setCommonErrors([])
    // @ts-ignore
    delete values["undefined"]
    graphqlClient.chain.mutation
      .register({...values, language})
      .execute({__scalar: 1})
      .then(() => navigate(routes.LOGIN+'?registered=true'))
      .catch(e => {
        setSubmitting(false)
        const extensions = requireErrorExtensions(e)
        setCommonErrors(extensions.common_errors || [])
        setFieldErrors(extensions.field_errors || {})
      })
      .finally(() => setSubmitting(false))
  }

  if (user.data) return <Navigate to={routes.INDEX} />

  return (
    <mui.Container component="main" maxWidth="xs">
      <mui.Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={logoIcon} alt="logo" height="100"/>
        <mui.Typography component="h1" variant="h5" mt={4}>
          {t('registration__title')}
        </mui.Typography>
        <Formik
          initialValues={{
            adisseoContactName: '',
            email: '', 
            company: '',
            country: 'US',
            name: '',
            password: '',
            password2: '',
        }}
          onSubmit={handleSubmit}
          sx={{mt: 1}}
          validateOnBlur={true}
          validateOnChange={false}
          validationSchema={validationSchema}
        >
          {({submitForm, isSubmitting, touched, errors, isValid}) => (
            <Form style={{width: '100%'}}>
              <mui.Stack spacing={3} mt={3}>
                {commonErrors.map((error, i) => (
                  <mui.Alert key={i} severity="error">
                    {t(`api__${error}`)}
                  </mui.Alert>
                ))}
                <Field
                  component={TextField}
                  error={touched.name && (!!errors.name || !!fieldErrors.name)}
                  helperText={touched.name && (errors.name || fieldErrors.name)}
                  InputProps={{fullWidth: true}}
                  label={t('registration__field__name')}
                  name="name"
                />
                <Field
                  component={TextField}
                  error={touched.company && (!!errors.company || !!fieldErrors.company)}
                  helperText={touched.company && (errors.company || fieldErrors.company)}
                  InputProps={{fullWidth: true}}
                  label={t('registration__field__company')}
                  name="company"
                />
                <Field
                  component={TextField}
                  error={touched.adisseoContactName && (!!errors.adisseoContactName || !!fieldErrors.adisseoContactName)}
                  helperText={touched.adisseoContactName && (errors.adisseoContactName || fieldErrors.adisseoContactName)}
                  InputProps={{fullWidth: true}}
                  label={t('registration__field__adisseo_contact_name')}
                  name="adisseoContactName"
                />
                <Field
                  component={Select}
                  name="country"
                  error={touched.country && (!!errors.country || !!fieldErrors.country)}
                  helperText={touched.country && (errors.country || fieldErrors.country)}
                >
                  {(countries.data || [{code: 'US', name: 'United States of America'}]).map((country, i) => (
                    <mui.MenuItem key={i} value={country.code}>
                      {country.name}
                    </mui.MenuItem>
                  ))}
                </Field>
                <Field
                  component={TextField}
                  error={touched.email && (!!errors.email || !!fieldErrors.email)}
                  helperText={touched.email && (errors.email || fieldErrors.email)}
                  InputProps={{autoComplete: 'email', fullWidth: true}}
                  InputLabelProps={{shrink: true}}
                  label={t('registration__field__email')}
                  name="email"
                />
                <Field
                  component={TextField}
                  error={touched.password && (!!errors.password || !!fieldErrors.password)}
                  helperText={touched.password && (errors.password || fieldErrors.password)}
                  InputProps={{autoComplete: 'off', fullWidth: true, type: 'password'}}
                  label={t('registration__field__password_1')}
                  name="password"
                />
                <Field
                  component={TextField}
                  error={touched.password2 && (!!errors.password2 || !!fieldErrors.password2)}
                  helperText={touched.password2 && (errors.password2 || fieldErrors.password2)}
                  InputProps={{autoComplete: 'off', fullWidth: true, type: 'password'}}
                  label={t('registration__field__password_2')}
                  name="password2"
                />
                <mui.Button
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    Object.keys(fieldErrors).length > 0 ||
                    Object.keys(touched).length === 0
                  }
                  fullWidth
                  onClick={submitForm}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    backgroundColor: '#A8C02A',
                    '&:hover': {
                      backgroundColor: '#C1CC00',
                    },
                }}
                  variant="contained"
                >
                  {t('registration__button__submit')}
                </mui.Button>
                <mui.Stack direction="row" justifyContent="space-between">
                  <mui.Stack>
                    <Link to={routes.LOGIN} variant="body2">
                      {t('registration__link__login')}
                    </Link>
                  </mui.Stack>

                  <LanguageSelector />
                </mui.Stack>
              </mui.Stack>
            </Form>
          )}
        </Formik>
      </mui.Box>
    </mui.Container>
  )
}